(() => {
  const SELECTORS = {
    header: '.section-header, sticky-header.header-wrapper, .hindi-header-wrapper',
    scrollers: '.hindi-categories__scroller, .hindi-collection-nav__track, .hindi-product-service-strip',
  };

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let ticking = false;

  const updateScrolledState = () => {
    document.body?.classList.toggle('is-scrolled', window.scrollY > 12);
    ticking = false;
  };

  const requestScrollUpdate = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(updateScrolledState);
  };

  const updateHeaderHeight = () => {
    const candidates = Array.from(document.querySelectorAll(SELECTORS.header));
    const visible = candidates.find((element) => {
      const rect = element.getBoundingClientRect();
      return rect.height > 0 && getComputedStyle(element).display !== 'none';
    });

    if (!visible) return;
    const height = Math.ceil(visible.getBoundingClientRect().height);
    document.documentElement.style.setProperty('--hindi-header-height', `${height}px`);
  };

  const makeScrollerKeyboardFriendly = (scroller) => {
    if (!scroller || scroller.dataset.hindiKeyboardScroller === 'true') return;
    scroller.dataset.hindiKeyboardScroller = 'true';

    if (!scroller.hasAttribute('tabindex')) scroller.tabIndex = 0;

    scroller.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      const direction = event.key === 'ArrowRight' ? 1 : -1;
      const rtl = document.documentElement.dir === 'rtl';
      const amount = Math.max(scroller.clientWidth * 0.72, 220);

      event.preventDefault();
      scroller.scrollBy({
        left: amount * direction * (rtl ? -1 : 1),
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
    });
  };

  const optimiseImages = (root = document) => {
    root.querySelectorAll('img[loading="lazy"]:not([decoding])').forEach((image) => {
      image.decoding = 'async';
    });
  };

  const ARABIC_RE = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/;
  const LATIN_RE = /[A-Za-z]/;

  const classifyLanguage = (text = '') => {
    const value = text.replace(/\s+/g, ' ').trim();
    if (!value) return 'other';

    const hasArabic = ARABIC_RE.test(value);
    const hasLatin = LATIN_RE.test(value);

    if (hasArabic && !hasLatin) return 'ar';
    if (hasLatin && !hasArabic) return 'en';
    if (hasArabic && hasLatin) return 'mixed';
    return 'other';
  };

  const createDescriptionNode = (html, sourceTag = 'p') => {
    const tagName = ['p', 'div', 'li'].includes(sourceTag?.toLowerCase()) ? sourceTag.toLowerCase() : 'p';
    const node = document.createElement(tagName);
    node.innerHTML = html;
    return node;
  };

  const normaliseMixedNode = (node) => {
    const html = node.innerHTML || '';
    const parts = html
      .split(/<br\s*\/?>(?:\s*)/i)
      .map((part) => part.trim())
      .filter(Boolean);

    if (parts.length < 2) return null;

    return parts.map((part) => ({
      type: classifyLanguage(part),
      node: createDescriptionNode(part, node.tagName),
    }));
  };

  const separateSpotlightBilingualDescriptions = (root = document) => {
    root.querySelectorAll('[data-hindi-spotlight-bilingual]').forEach((container) => {
      if (!container || container.dataset.hindiSpotlightSeparated === 'true') return;

      const text = (container.textContent || '').replace(/\s+/g, ' ').trim();
      if (!text || !ARABIC_RE.test(text) || !LATIN_RE.test(text)) return;

      const arabicIndex = text.search(ARABIC_RE);
      const latinIndex = text.search(LATIN_RE);
      let first = '';
      let second = '';
      let firstLang = '';
      let secondLang = '';

      if (arabicIndex < latinIndex) {
        const punctuationMatch = text.match(/[.!؟]\s*[A-Za-z]/);
        const splitIndex = punctuationMatch
          ? punctuationMatch.index + punctuationMatch[0].search(/[A-Za-z]/)
          : latinIndex;
        first = text.slice(0, splitIndex).trim();
        second = text.slice(splitIndex).trim();
        firstLang = 'ar';
        secondLang = 'en';
      } else {
        const punctuationMatch = text.match(/[.!?]\s*[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/);
        const splitIndex = punctuationMatch
          ? punctuationMatch.index + punctuationMatch[0].search(/[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/)
          : arabicIndex;
        first = text.slice(0, splitIndex).trim();
        second = text.slice(splitIndex).trim();
        firstLang = 'en';
        secondLang = 'ar';
      }

      if (!first || !second) return;

      const wrapper = document.createElement('div');
      wrapper.className = 'hindi-dish-spotlight__description-languages';

      const makeBlock = (content, lang) => {
        const block = document.createElement('p');
        block.className = `hindi-dish-spotlight__description-language hindi-dish-spotlight__description-language--${lang}`;
        block.lang = lang;
        block.dir = lang === 'ar' ? 'rtl' : 'ltr';
        block.textContent = content;
        return block;
      };

      wrapper.appendChild(makeBlock(first, firstLang));
      wrapper.appendChild(makeBlock(second, secondLang));
      container.innerHTML = '';
      container.appendChild(wrapper);
      container.dataset.hindiSpotlightSeparated = 'true';
    });
  };

  const separateBilingualProductDescriptions = (root = document) => {
    root.querySelectorAll('[data-hindi-bilingual-description]').forEach((container) => {
      if (!container || container.dataset.hindiSeparated === 'true') return;

      const children = Array.from(container.children).filter((child) => child.textContent.trim() !== '');
      if (!children.length) return;

      const groups = { ar: [], en: [], other: [] };

      children.forEach((child) => {
        const type = classifyLanguage(child.textContent || '');

        if (type === 'mixed') {
          const parts = normaliseMixedNode(child);
          if (parts) {
            parts.forEach((part) => {
              if (part.type === 'ar' || part.type === 'en') {
                groups[part.type].push(part.node);
              } else {
                groups.other.push(part.node);
              }
            });
            return;
          }
        }

        if (type === 'ar' || type === 'en') {
          groups[type].push(child.cloneNode(true));
        } else {
          groups.other.push(child.cloneNode(true));
        }
      });

      if (!groups.ar.length || !groups.en.length) return;

      const wrapper = document.createElement('div');
      wrapper.className = 'hindi-product-description__bilingual';

      const appendGroup = (lang, nodes) => {
        if (!nodes.length) return;
        const group = document.createElement('div');
        group.className = `hindi-product-description__lang-group hindi-product-description__lang-group--${lang}`;
        nodes.forEach((node) => group.appendChild(node));
        wrapper.appendChild(group);
      };

      appendGroup('ar', groups.ar);
      appendGroup('en', groups.en);

      if (groups.other.length) {
        const misc = document.createElement('div');
        misc.className = 'hindi-product-description__lang-group hindi-product-description__lang-group--other';
        groups.other.forEach((node) => misc.appendChild(node));
        wrapper.appendChild(misc);
      }

      container.innerHTML = '';
      container.appendChild(wrapper);
      container.dataset.hindiSeparated = 'true';
    });
  };

  const initialise = (root = document) => {
    root.querySelectorAll(SELECTORS.scrollers).forEach(makeScrollerKeyboardFriendly);
    optimiseImages(root);
    separateBilingualProductDescriptions(root);
    separateSpotlightBilingualDescriptions(root);
    updateHeaderHeight();
    updateScrolledState();
    document.documentElement.dataset.hindiReady = 'true';
  };

  const start = () => {
    initialise();

    window.addEventListener('scroll', requestScrollUpdate, { passive: true });
    window.addEventListener('resize', updateHeaderHeight, { passive: true });

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(updateHeaderHeight);
      document.querySelectorAll(SELECTORS.header).forEach((header) => observer.observe(header));
    }

    document.addEventListener('shopify:section:load', (event) => initialise(event.target));
    document.addEventListener('shopify:section:reorder', updateHeaderHeight);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
